/**
 * OpenIntakeForm renderer — dispatches to New or Existing intake based on
 * action.params.form_kind. Passes prompts and wires save/cancel back to
 * the runner's advance flow.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import NewClientIntakeForm from '../intake/NewClientIntakeForm';
import ExistingClientIntakeForm from '../intake/ExistingClientIntakeForm';
import ManualClientShell from '../intake/ManualClientShell';

export default function OpenIntakeForm({ action, capturedData, value, onChange, onNext, onBack, onCancel, canBack,
                                          statusKey, taskId, onTemplateChanged }) {
  const params = action.params || {};
  const kind = params.form_kind || 'new_client';
  const navigate = useNavigate();

  // Draft auto-save: writes the current form state into inflight value
  // (which gets committed to captured_data via patch when the operator saves)
  const autoSaveDraft = (draft) => {
    onChange(draft);
  };

  const handleSave = (result) => {
    // result contains client_id, client_record, extra fields.
    // Pass the fresh value directly to onNext to avoid a stale-closure race
    // where advance() would otherwise read the previous (null) inflightValue.
    onChange(result);
    onNext(result);
  };

  // Iter303ai-fix4 · Cancel = smart back-or-abandon via the runner's
  // onCancel prop when available. Falls back to legacy behaviour for
  // any renderer that hasn't been upgraded yet.
  const handleCancel = () => {
    if (onCancel) return onCancel();
    if (canBack) return onBack();
    navigate('/workflow/start');
  };

  // Template edit context — enables the in-runner ✎ pencil edits and ↑↓
  // reorder controls inside the intake form. Falls back to disabled if the
  // runner didn't supply the ids.
  const editCtx = (statusKey && taskId && action?.id) ? {
    statusKey, taskId, actionId: action.id,
    headerTitle: params.header_title,
    headerDescription: params.header_description,
    onTemplateChanged,
  } : null;

  return (
    <div>
      {kind === 'existing_client' ? (
        <ExistingClientIntakeForm
          prompts={params.prompts || ''}
          capturedData={capturedData}
          onSave={handleSave}
          onCancel={handleCancel}
          autoSaveDraft={autoSaveDraft}
          editCtx={editCtx}
        />
      ) : params.mode === 'manual' ? (
        // iter303ak-fix49 · Manual client shell — creates a bare-minimum
        // client + auto-created property, then routes operator to the
        // full client edit page in a pop-out.
        <ManualClientShell
          existingClientId={capturedData?.client_id || null}
          onSave={handleSave}
          onCancel={handleCancel}
          headerTitle={params.header_title}
          headerDescription={params.header_description}
        />
      ) : (
        <NewClientIntakeForm
          initial={capturedData?.client_intake_form || value?.form || {}}
          existingClientId={capturedData?.client_id || null}
          existingPropertyId={capturedData?.property_id || null}
          prompts={params.prompts || ''}
          capturedData={capturedData}
          onSave={handleSave}
          onCancel={handleCancel}
          autoSaveDraft={autoSaveDraft}
          editCtx={editCtx}
        />
      )}
    </div>
  );
}
